import React, { useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import DocRenderer from './DocRenderer';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import images from '../constants/image.js';
import { Navbar } from "./Navbar.jsx";
import { toast } from "react-toastify";
import Cookies from "js-cookie"
import { motion } from "framer-motion";
import { ClipLoader } from 'react-spinners';
import EmailModal from "./EmailModal.jsx";

const pics = [
  {
    logo: images.PB5,
  },
];

const api = process.env.REACT_APP_API_URL;


function FullScore() {
  const [activeTab, setActiveTab] = useState('recommendations'); // Manage active tab state
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const { data, jobDescriptionFile, jobDescriptionText, resumeFile } = location.state || {};
  const [missingKeywords, setMissingKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [emailloader, setEmailLoader] = useState(false);

  const scoreInt = data && data.score !== undefined ? Math.round(data.score) : 0;
  const sessionId = data ? data.session_id : null;
  const message = scoreInt > 70 ? 'Excellent' : 'Needs Improvement';


  useEffect(() => {
    
    const fetchData = async () => {
      setLoadingRecommendations(true);
      setLoadingKeywords(true);

      try {
        const token = Cookies.get('access_token');
        if (!token) throw new Error('Access token is missing');


        const keywordsResponse = await fetch(`${api}missing-keywords/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },

          credentials: 'include',
        });

        if (!keywordsResponse.ok) {
          toast.error('Keywords error details');
        }

        const keywordsData = await keywordsResponse.json();
        setMissingKeywords(keywordsData.missing_keywords);



        // Fetch recommendations
        const recommendationsResponse = await fetch(`${api}recommendations/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });

        if (!recommendationsResponse.ok) {
          toast.error('Recommendations error details');
        }

        const recommendationsData = await recommendationsResponse.json();
        setRecommendations(recommendationsData.result);
      } catch (error) {
      } finally {
        setLoadingRecommendations(false);
        setLoadingKeywords(false);
      }
    };

    fetchData();
  }, [sessionId]);
  // Dependency on sessionId

  const handleEmailSubmit  = async (mail) => {
    

    try {
      const token = Cookies.get('access_token');
      if (!token) throw new Error('Access token is missing');

      const payload = { email: mail, session_id: sessionId };
      setEmailLoader(true);
      const response = await fetch(`${api}email/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        credentials: 'include',
      });


      if (!response.ok) {
        const errorDetails = await response.json();
        setEmailLoader(false);
        toast.error(`Email Error: ${errorDetails.message}`);
        throw new Error('Failed to send email');
        
      }
      setEmailLoader(false);

      toast.success('Email sent successfully!');

      // Fetch recommendations and missing keywords
      setLoadingRecommendations(true);
      setLoadingKeywords(true);

      const [recommendationsResponse, keywordsResponse] = await Promise.all([
        fetch(`${api}recommendations/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }),
        fetch(`${api}missing-keywords/`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        }),
      ]);

      if (!recommendationsResponse.ok || !keywordsResponse.ok) {
        toast.error('Failed to get data try again.');
        throw new Error('Error fetching recommendations or keywords');
      }

      const recommendationsData = await recommendationsResponse.json();
      const keywordsData = await keywordsResponse.json();

      setRecommendations(recommendationsData.result);
      setMissingKeywords(keywordsData.missing_keywords);
    } catch (error) {
    } finally {
      setLoadingRecommendations(false);
      setLoadingKeywords(false);
      setShowModal(false);
    }
  };




  const RecommendationsRenderer = ({ recommendations }) => {
    if (!recommendations) return null;
    const { sections, recommendations: recs, keywords_bridged: keywords } = recommendations;

    const containerVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.3 } },
    };

    const itemVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    };

    return (
      <motion.div
        className="flex flex-col bg-white h-fit max-w-3xl w-full mx-auto rounded-md p-6 space-y-8 shadow-lg"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <p className="text-gray-400"> Note: The following recommendations provide targeted insights to align your resume with the job description, emphasizing missing keywords and impactful phrasing to enhance ATS compatibility. Use these actionable steps to refine your resume, showcase your skills effectively, and increase your chances of securing the role. For future reference, email the recommendation to save a copy.</p>
        {/* Recommendations - Summary */}
        {recs?.summary?.length > 0 && (
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Summary</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-900">
              {recs.summary.map((item, index) => (
                <motion.li key={index} className="text-gray-900" variants={itemVariants}>
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Recommendations - Technical Skills */}
        {recs?.technical_skills?.length > 0 && (
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Technical Skills</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-900">
              {recs.technical_skills.map((skill, index) => (
                <motion.li key={index} className="text-gray-900" variants={itemVariants}>
                  {skill}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}


        {/* Professional Experience */}
        {sections?.experience?.companies?.length > 0 && (
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Professional Experience</h2>
            {sections.experience.companies.map((company, index) => (
              <motion.div key={index} className="mb-4">
                <h3 className="text-lg font-bold text-gray-900">{company}</h3>
                {recs?.experience?.[company]?.length > 0 && (
                  <ul className="list-disc pl-5 space-y-2">
                    {recs.experience[company].map((item, idx) => (
                      <motion.li key={idx} className="text-gray-900" variants={itemVariants}>
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Education */}
        {recs?.education?.length > 0 && (
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Education</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-900">
              {recs.education.map((item, index) => (
                <motion.li key={index} className="text-gray-900" variants={itemVariants}>
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {keywords?.length > 0 && (
          <motion.div variants={itemVariants}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">Keywords Bridged</h2>
            <div className="grid grid-cols-2 gap-4">
              {keywords.map((keyword, index) => (
                <motion.div
                  key={index}
                  className="flex items-center bg-gray-100 p-3 rounded-lg shadow-sm"
                  variants={itemVariants}
                >
                  <svg
                    className="w-6 h-6 text-green-500 flex-shrink-0 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-900 font-medium">{keyword}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}


      </motion.div>
    );
  };



  return (
    <>
      <Navbar />
      <div className="flex flex-col min-h-screen bg-white md:pl-16 md:pr-16">


        {/* Progress Bar Section */}
        <div className="flex justify-center w-full">
          <img src={pics[0].logo} alt="Progress Bar" className="max-w-full h-auto" />
        </div>

        <div className="flex justify-center text-lg font-medium text-black md:mt-4">
          Resume Score
        </div>

        <div className="flex flex-col md:flex-row bg-transparent min-h-screen min-w-screen md:mt-20">
          {/* Left Section */}
          <div className="flex flex-col flex-grow md:w-1/2">
            <div className="bg-white max-w-2xl w-full mt-16 md:-mt-14 mx-auto rounded-md p-6">
              <div className="flex flex-col items-center">
                <div className="relative w-40 h-40">
                  {/* Full Circle Background */}
                  <div className="relative w-40 h-40">
                    <div className="absolute rounded-full border-4 bg-white border-gray-300"></div>
                    <div
                      className="absolute w-full h-full rounded-full border-4 bg-white border-green-500"
                      style={{
                        clipPath: "circle(50% at 50% 50%)",
                        background: `conic-gradient(
                        #4ade80 ${scoreInt * 3.6}deg, 
                        #e5e7eb ${(100 - scoreInt) * 3.6}deg
                      )`,
                      }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-black">{scoreInt}%</span>
                    </div>
                  </div>
                </div>
                <div className="text-center text-lg font-semibold text-black">{message}</div>
                <div className="text-center text-black mt-2">
                  Keep working on your resume to get better results and even better opportunities
                </div>
              </div>
            </div>

            <div className="bg-white max-w-2xl w-full mx-auto mt-4 rounded-md p-6">
              <div className="text-center text-md font-semibold text-gray-600">Missing Keywords</div>
              <div className="text-center text-black mt-2">
                {loadingKeywords ? (
                  <div className="h-[40vh] flex justify-center items-center bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <ClipLoader color="#f97316" size={50} />
                      <div className="mt-4 text-lg font-medium">Loading Keywords...</div>
                    </div>

                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center items-center gap-4">
                    {missingKeywords.map((keyword, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-md shadow-sm"
                      >
                        <span className="text-black font-medium">{keyword}</span>
                        <span className="text-green-500">✅</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Section */}
          <div className="flex flex-col flex-grow mt-32 md:-mt-14 mx-4 md:mx-6 rounded-md">
            <div className="flex flex-wrap justify-around p-2 bg-white w-full pl-4 pr-4 pt-2 rounded-lg mb-3">
              <button
                className={`flex-grow md:flex-none w-full md:w-36 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${activeTab === 'resume' ? 'bg-orange-500 text-white' : 'hover:bg-gray-700 active:bg-orange-500'}`}
                onClick={() => setActiveTab('resume')}
              >
                Resume
              </button>

              <button
                className={`flex-grow md:flex-none w-full md:w-48 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${activeTab === 'jobDescription' ? 'bg-orange-500 text-white' : 'hover:bg-gray-400 active:bg-orange-500'}`}
                onClick={() => setActiveTab('jobDescription')}
              >
                Job Description
              </button>

              <button
                className={`flex-grow md:flex-none w-full md:w-36 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
      ${activeTab === 'recommendations' ? 'bg-orange-500 text-white' : 'hover:bg-gray-400 active:bg-orange-500'}`}
                onClick={() => setActiveTab('recommendations')}
              >
                Recommendations
              </button>

              <button
                className={`flex-grow md:flex-none w-full md:w-20 text-black bg-gray-300 text-center py-2 rounded-lg mb-3 mt-2 transition-colors duration-200 ease-in-out
  hover:bg-gray-400 active:bg-orange-500`}
                onClick={() => setShowModal(true)}
              >
                Email Me
              </button>
            </div>




            <div className="w-full max-w-screen-md bg-white shadow-md overflow-auto rounded-lg p-5 mx-auto">
              {loadingRecommendations ? (
                <div className="h-[40vh] flex justify-center items-center bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <ClipLoader color="#f97316" size={50} />
                    <div className="mt-4 text-lg font-medium">Loading Recommendations...</div>
                  </div>

                </div>
              ) : activeTab === 'recommendations' ? (
                <RecommendationsRenderer recommendations={recommendations} />
              ) : activeTab === 'resume' ? (
                resumeFile ? <DocRenderer file={resumeFile} /> : <div>No resume uploaded.</div>
              ) : (
                jobDescriptionFile ? (
                  <DocRenderer file={jobDescriptionFile} />
                ) : jobDescriptionText ? (
                  <div>{jobDescriptionText}</div>
                ) : (
                  <div>No job description uploaded.</div>
                )
              )}
            </div>
          </div>
        </div>

        {showModal && (
          <EmailModal
          onClose={() => setShowModal(false)}
          onSubmit={handleEmailSubmit}
          emailloader={emailloader}
        />
        )}
      </div>
    </>
  );

}

export default FullScore;
